import React, { useState, useEffect } from 'react';
import leftIcon from '../assets/left-icon.png';
// The distributor panel carries no price, so a dollar on its reopen tab would
// promise something the panel never shows.
import infoIcon from '../assets/images/noun_Info.png';
import { generateItemListPDF } from './GenerateItemListPDF';
import { richTextToPlain } from '../utils/richText';

// Gap between the bordered box's right edge and the panel's right edge, and the
// rendered width of the collapse arrow. The arrow is positioned from these two
// so its left edge lands exactly on the border line — deriving it rather than
// hard-coding an offset is what keeps Edge, Firefox and Chrome identical.
const INSET_RIGHT = 25;
// Matching gap on the left. It was written inline in the inset string; the
// distributor width now has to add both gutters back, so it needs a name.
const INSET_LEFT = 28;
const ARROW_W = 20;
// How far the arrow tucks under the 3px white border. The arrow image has
// rounded left corners; overlapping the border by 2px hides them so the tab
// reads as growing straight out of the border line.
const ARROW_OVERLAP = 2;
// Same 48×72 proportion as the old arrow image, so the tab keeps its size.
const ARROW_H = 30;

// Everything the distributor panel adds is taken out of
// visualizer-manufacturer-side-bar-mockup_9.18.26.psd, not eyeballed off a
// screenshot. The PSD is 976×2143 with the panel's outer rectangle at
// (209,183)–(752,1920) — 543×1737pt — and every number below is that
// rectangle's coordinate space.
const PSD_PANEL_W = 543;
// Width of both title lines in the PSD (the 452pt block less 33pt of padding
// each side). They are the same width there, and that is what makes
// VISUALIZER line up with CUSTOM CABINETRY PROJECT — so it is a width to hit,
// not a font size to copy. The live block is psd(449) rather than psd(452),
// because the PSD's block is 3pt off-centre inside the panel and ours is
// centred, so the slot is psd(383); 380 keeps a hair of room inside it for
// font-metric drift, which a nowrap line would otherwise spill over.
const PSD_TITLE_W = 380;
// Ink width per 1px of font-size, measured in the browser with the loaded
// Bebas Neue at the PSD's tracking. The PSD was set in Bebas-Regular, which
// is ~8% wider, so copying its 42pt / 75pt straight across left both lines
// well short of the block — the empty gold margin the client flagged.
// Dividing the target width by these lands each line on it instead.
const BEBAS_INK_TOP = 8.42;    // 'CUSTOM CABINETRY PROJECT' at -0.01em
const BEBAS_INK_MAIN = 4.8961; // 'VISUALIZER' at 0.165em

// "Rectangle Main Large" and "Rectangle - Cost Estimator" both carry this
// solid fill: Rd 157.998, Grn 129.998, Bl 55.999.
const GOLD = '#9E8238';

// Listed between Style & Grain and the appliance. None of them is something the
// customer picks in the visualizer, and all are fixed for every order today —
// the first two are the pair the cost snapshot's disclaimer already names — so
// they are stated here rather than read off a selection that does not exist.
// Temporary: when the API starts returning them per order, drop this and let
// them flow through with the rest.
const FIXED_BOX_ROWS = [
  { category: 'Cabinet Box Material', collection: '', value: 'UV Maple' },
  { category: 'Drawer Box Type', collection: '', value: 'Maple Standard Grade' },
  { category: 'Additional Handleless Option', collection: '', value: 'GOLA Handleless System' },
];

export function normalizePriceData(raw) {
  if (!raw) return null;

  return {
    total_price: raw.price ?? null,
    linear_feet: raw.total_lf ?? null,
    price_per_lf: raw.lf_price ?? null,
    design_url: raw.design_url ?? null,
    item_list_url: raw.item_list_url ?? null,
    lines: raw.lines ?? [],
  };
}

export function PricePanel({
  finalArray,
  priceData,
  isPriceLoading,
  isPanelOpen,
  setIsPanelOpen,
  // 'price' | 'distributor' | 'none'. Only a distributor's admin moves it off
  // 'price'; the ordinary visualizer never passes anything else.
  panelMode = 'price',
  // Shown in place of the price box on the distributor panel — the layout's
  // own label, e.g. "GALAXY KITCHEN".
  layoutLabel = '',
  // The layout's collections, each with the name the selections are keyed by
  // and the label an admin typed for it. Only the distributor panel uses them.
  collections = [],
  hideGrain = false,
}) {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [NAV_H, setNAV_H] = useState(53.6);

  useEffect(() => {
    const updateNavHeight = () => {
      const nav = document.getElementById('main-nav');
      if (nav) {
        setNAV_H(nav.getBoundingClientRect().height);
      }
    };

    updateNavHeight();

    const nav = document.getElementById('main-nav');
    if (nav) {
      const ro = new ResizeObserver(updateNavHeight);
      ro.observe(nav);
      return () => ro.disconnect();
    }
  }, []);

  const data =
    priceData &&
      priceData.total_price === undefined &&
      priceData.price !== undefined
      ? normalizePriceData(priceData)
      : priceData;

  // Any "… Cabinets" selection is a door category. This used to be a hardcoded
  // list of Base/Wall/Tall/Island, which left Vanity Cabinets out — so the
  // bathroom printed "Tall Cabinets" and "Vanity Cabinets" as two rows even when
  // both carried the same collection and colour, instead of merging them the way
  // the kitchen merges Base/Wall/Tall/Island.
  const isCabinetSelection = (s) =>
    s?.parentName?.trim().toLowerCase().endsWith('cabinets');

  // Preferred display order; anything else matching isCabinetSelection is
  // appended after these, so a new cabinet type still gets merged.
  const CABINET_ORDER = [
    'Base Cabinets',
    'Wall Cabinets',
    'Tall Cabinets',
    'Island Cabinets',
    'Vanity Cabinets',
  ];

  const orderedCabinets = () => {
    const cabinets = (finalArray || []).filter(isCabinetSelection);
    const ranked = [...cabinets].sort((a, b) => {
      const ia = CABINET_ORDER.indexOf(a.parentName);
      const ib = CABINET_ORDER.indexOf(b.parentName);
      return (ia === -1 ? CABINET_ORDER.length : ia) - (ib === -1 ? CABINET_ORDER.length : ib);
    });
    return ranked;
  };

  const buildCabinetSelections = () => {
    if (!finalArray || finalArray.length === 0) return {};

    const cab = finalArray.find(isCabinetSelection);

    const firstLine = data?.lines?.[0]?.msg;

    return {
      collection: firstLine?.collection || cab?.categoryName || '(Collection Name)',
      door_color: firstLine?.door_color || cab?.imageName || '(Color Name)',
      grain: firstLine?.grain || '(Style & Grain Direction)',
      box_material: firstLine?.box_material || '(Box Material Name)',
      drawer_box: firstLine?.drawer_box || '(Drawer Box Type)',
    };
  };

  const formatCategoryList = (arr) => {
    if (!arr || arr.length === 0) return '';
    if (arr.length === 1) return arr[0];
    if (arr.length === 2) return `${arr[0]} & ${arr[1]}`;
    return `${arr.slice(0, -1).join(', ')} & ${arr[arr.length - 1]}`;
  };

  const processSelections = () => {
    if (!finalArray || finalArray.length === 0) {
      return { mainSelections: [] };
    }

    const otherSelections = finalArray.filter((s) => !isCabinetSelection(s));

    const processedSelections = [];
    const cabinetMap = {};

    orderedCabinets().forEach((cab) => {
      if (!cab) return;
      const key = `${cab.categoryName}|${cab.imageName}`;
      if (!cabinetMap[key]) {
        cabinetMap[key] = { categories: [], collection: cab.categoryName, value: cab.imageName };
      }
      cabinetMap[key].categories.push(cab.parentName);
    });

    Object.values(cabinetMap).forEach((item) => {
      const shortNames = item.categories.map((n) => n.replace(' Cabinets', ''));
      processedSelections.push({
        category: `${formatCategoryList(shortNames)} Cabinets`,
        collection: item.collection,
        value: item.value,
      });
    });

    otherSelections.forEach((sel) => {
      processedSelections.push({
        category: sel.parentName,
        collection: sel.categoryName,
        value: sel.imageName,
      });
    });

    const hidden = ['backsplash', 'countertop', 'flooring'];
    // When every required cabinet resolved to a NoGrain door there is no grain
    // direction to report, so drop the "Style & Grain" row entirely rather than
    // printing a direction the selection doesn't actually have.
    if (hideGrain) hidden.push('style & grain');

    const mainSelections = processedSelections.filter(
      (s) =>
        !s.category.toLowerCase().includes('box') &&
        !s.category.toLowerCase().includes('hardware') &&
        !hidden.includes(s.category.toLowerCase())
    );

    return { mainSelections };
  };

  const { mainSelections } = processSelections();

  // Both panels list them, and in the place the PSD puts them: after Style &
  // Grain, before the appliance. Appending blindly would land them after the
  // appliance instead.
  const listedSelections = (() => {
    const applianceAt = mainSelections.findIndex((sel) =>
      sel.category.toLowerCase().startsWith('appliance')
    );
    const at = applianceAt === -1 ? mainSelections.length : applianceAt;
    return [
      ...mainSelections.slice(0, at),
      ...FIXED_BOX_ROWS,
      ...mainSelections.slice(at),
    ];
  })();

  const handleButtonClick = async (label) => {
    if (label === 'COST ESTIMATOR') {
      window.open(
        'https://www.domaincabinets.com/custom-cost-estimator-a/449.htm',
        '_blank',
        'noopener,noreferrer'
      );
      return;
    }

    if (label === 'DESIGN' && data?.design_url) {
      window.open(data.design_url, '_blank');
      return;
    }

    if (label === 'ITEM LIST') {
      if (data?.item_list_url) {
        window.open(data.item_list_url, '_blank');
        return;
      }
      try {
        setIsGeneratingPDF(true);
        await generateItemListPDF(data, buildCabinetSelections());
      } catch (err) {
        console.error('PDF generation error:', err);
      } finally {
        setIsGeneratingPDF(false);
      }
    }
  };

  // "No side bar" means gone, not collapsed: the reopen tab would otherwise
  // still sit on the screen advertising a panel that must not be shown.
  if (panelMode === 'none') return null;

  // Everything below branches on this and nothing else. The cost-snapshot
  // markup is left exactly as it was — the distributor variant swaps the
  // header, drops the price box and the price disclaimer that goes with it,
  // and adds the estimator call-to-action underneath.
  const isDistributor = panelMode === 'distributor';

  // Selections carry a collection's NAME ("Shinnoki"); the distributor panel
  // shows the label an admin set for it instead ("Shinnoki by DECOSPAN"), which
  // is the same thing the visualizer's own picker shows. The cost snapshot is
  // left on the name it has always printed.
  // Flattened to plain text because the label is stored as HTML — the editor can
  // put sub/sup in it — and this is a text row, not innerHTML.
  const collectionLabel = (name) => {
    if (!isDistributor || !name) return name;
    const match = collections.find(
      (c) => c?.name?.trim().toLowerCase() === String(name).trim().toLowerCase()
    );
    return richTextToPlain(match?.label) || name;
  };

  // Both panels are the same width — the dark strip has to read the same
  // whichever side bar a distributor is showing.
  const SHELL_W = `calc((100vh - ${NAV_H}px) * 0.356)`;
  // Width of the bordered box: the panel less the two gutters `inset` takes.
  const BOX_W = `calc(${SHELL_W} - ${INSET_LEFT + INSET_RIGHT}px)`;
  // One scale for the whole mock: the live box's width over the PSD's 543pt.
  // It used to be a frozen 0.4267, measured once against a 741px-tall panel
  // — on any taller window every element stayed that size and left the empty
  // band in the middle that the client flagged. Taken off the width rather
  // than the height because the width is what the text has to fit inside.
  const psd = (pt) => `calc(${BOX_W} * ${(pt / PSD_PANEL_W).toFixed(7)})`;

  return (
    <>
      {!isPanelOpen && (
        <button
          onClick={() => setIsPanelOpen(true)}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '0px',
            zIndex: 103,
            background: '#fff',
            border: 'none',
            cursor: 'pointer',
            padding: '10px 12px',
            boxShadow: '-2px 2px 8px rgba(0,0,0,0.4)',
            borderRadius: '4px 0 0 4px',
          }}
        >
          {/* Same 24px for both marks, as the tab has always used. */}
          <img
            src={isDistributor ? infoIcon : leftIcon}
            alt="open"
            style={{ width: '24px' }}
          />
        </button>
      )}

      <div
        style={{
          position: 'fixed',
          top: `${NAV_H}px`,
          right: 0,
          height: `calc(100vh - ${NAV_H}px)`,
          // Width stated directly instead of letting aspect-ratio derive it from
          // the height. Same 0.356 proportion, but aspect-ratio resolution differs
          // between engines, and any sub-pixel difference in the height got
          // multiplied into the width — which is why Edge's panel came out
          // narrower than Chrome's and Firefox's and wrapped the disclaimer onto
          // a fifth line. A width calc resolves identically everywhere.
          width: SHELL_W,
          background: 'rgba(0,0,0,0.84)',
          zIndex: 101,
          transform: isPanelOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease-in-out',
          boxSizing: 'border-box',
          containerType: 'inline-size',
        }}
      >
        {/* Outer border box — full flex column */}
        <div
          style={{
            position: 'absolute',
            // inset alone defines this box. It used to also carry `height` and
            // `aspect-ratio`, which is three constraints for two dimensions —
            // over-constrained, and each engine broke the tie differently, so the
            // right edge landed at a different x per browser. That is what made
            // the collapse arrow touch in one browser, overlap in another and gap
            // in a third. With only inset, the gutter is exactly INSET_RIGHT
            // everywhere. (The old clamps were no-ops: clamp(25px,1.73vw,25px)
            // is always 25, and clamp(28px,1.73vw,25px) has max < min so it
            // always returns 28. Written literally now so it reads honestly.)
            inset: `25px ${INSET_RIGHT}px 25px ${INSET_LEFT}px`,
            border: '3px solid #fff',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Inner padding wrapper — flex column, fills height */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              // PSD: the gold block sits 47pt in from the panel edge and 66pt
              // down from it. 3px of that inset is the box's own border.
              padding: isDistributor
                ? `${psd(66)} calc(${psd(47)} - 3px)`
                : 'calc(clamp(14px, 20.5px, 21px) * 2) clamp(18px, 2vw, 18px)',
              display: 'flex',
              flexDirection: 'column',
              boxSizing: 'border-box',
              overflow: 'hidden',
            }}
          >
            {/* ── TITLE — fixed, never shrinks ── */}
            <div
              style={{
                flexShrink: 0,
                textAlign: 'center',
              }}
            >
              {isDistributor ? (
                <>
                  {/* PSD "Rectangle Main Large": 452×218pt, 5pt corners, both
                      title lines centred inside it. */}
                  <div
                    style={{
                      background: GOLD,
                      borderRadius: psd(5),
                      height: psd(218),
                      padding: `0 ${psd(33)}`,
                      width: '100%',
                      boxSizing: 'border-box',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <p
                      style={{
                        fontFamily: "'Bebas Neue', sans-serif",
                        fontSize: psd(PSD_TITLE_W / BEBAS_INK_TOP),
                        fontWeight: '100',
                        // PSD tracking -10.
                        letterSpacing: '-0.01em',
                        color: '#fff',
                        lineHeight: 1,
                        // One line in the PSD; the panel is wide enough for it.
                        whiteSpace: 'nowrap',
                        margin: 0,
                        marginBottom: psd(38),
                      }}
                    >
                      CUSTOM CABINETRY PROJECT
                    </p>
                    {/* 75pt like COST SNAPSHOT, but tracked out to 165 — that
                        wide spacing is the whole difference between the two
                        headings in the PSD. */}
                    <h1
                      style={{
                        fontFamily: "'Bebas Neue', sans-serif",
                        fontSize: psd(PSD_TITLE_W / BEBAS_INK_MAIN),
                        whiteSpace: 'nowrap',
                        fontWeight: '100',
                        letterSpacing: '0.165em',
                        color: '#fff',
                        lineHeight: 0.96,
                        margin: 0,
                        // Tracking puts a gap after the last letter, so the line
                        // box is 0.165em wider than the word and no longer fits
                        // the slot — centring then does nothing and the word
                        // drifts right. A negative right margin lets that dead
                        // space hang outside, which leaves the word itself
                        // spanning the slot exactly, edge to edge with the line
                        // above. (textIndent used to shift it instead, which
                        // moved the word without shrinking the line.)
                        marginRight: '-0.165em',
                      }}
                    >
                      VISUALIZER
                    </h1>
                  </div>

                  {/* 60pt Bebas, tracking 50, sitting where the price box sits
                      on the cost snapshot. */}
                  <h2
                    style={{
                      fontFamily: "'Bebas Neue', sans-serif",
                      fontSize: psd(60),
                      whiteSpace: 'nowrap',
                      fontWeight: '100',
                      letterSpacing: '0.05em',
                      color: '#fff',
                      lineHeight: 1,
                      margin: 0,
                      marginTop: psd(83),
                      marginBottom: psd(63),
                    }}
                  >
                    {layoutLabel}
                  </h2>
                </>
              ) : (
                <>
              <p
                style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: 'clamp(17px, 1.0vw, 17px)',
                  fontWeight: '100',
                  letterSpacing: '0.06em',
                  color: '#fff',
                  lineHeight: 1,
                  margin: 0,
                  paddingTop: '2px',
                  marginBottom: '5%',
                }}
              >
                CUSTOM CABINETRY PROJECT
              </p>
              <h1
                style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: 'clamp(32px, 3.2vw, 30px)',
                  whiteSpace: 'nowrap',
                  fontWeight: '100',
                  letterSpacing: '0.03em',
                  color: '#fff',
                  lineHeight: 0.96,
                  margin: '0px 0px -4px',
                  
                               }}
              >
                COST SNAPSHOT
              </h1>
                </>
              )}
            </div>

            {/* ── PRICE BOX — fixed, never shrinks ── */}
            {!isDistributor && (
            <div
              style={{
                flexShrink: 0,
                background: '#fff',
                padding: 'clamp(6px, 1.4vw, 12px) ',
                marginTop: 'clamp(14px, 1.6vw, 19px)',
                marginBottom: 'clamp(14px, 1.6vw, 17px)',
                borderRadius: '2px',
                width: '100%',
                boxSizing: 'border-box',
                textAlign: 'center',
              }}
            >
              {isPriceLoading ? (
                <>
                  <div style={{ fontFamily: "'Calibri',sans-serif", fontSize: 'clamp(16px, 1.5vw, 30px)', color: '#999', textAlign: 'center' }}>
                    Calculating...
                  </div>
                 
                </>
              ) : (
                <div style={{ fontFamily: "'Calibri','Lato',sans-serif", color: '#111', lineHeight: 1, gap: '1.5%' }}>
                  <span style={{ fontSize: 'clamp(16px, 1.5vw, 38.7px)', fontWeight: '400', paddingTop: '0.58em', lineHeight: 1, marginRight: '0.25em' }}>$</span>
                  <span style={{ fontSize: 'clamp(20px, 2.5vw, 45px)', fontWeight: '400' }}>
                    {data?.total_price ? Math.round(Number(data.total_price)).toLocaleString() : '—'}
                  </span>
                  <span style={{ fontSize: 'clamp(14px, 2.5vw, 19px)', position: 'relative', top: '-0.57em', lineHeight: 1,paddingLeft:'2px',fontWeight:'600' }}>*</span>
                </div>
              )}
              <div style={{ fontFamily: "'Calibri','Lato',sans-serif", color: '#111',  marginTop:'-5px'}}>
                  <span style={{ fontSize: 'clamp(16px, 1.5vw, 30px)', fontWeight: '500', paddingTop: '0.55em', lineHeight: 1, marginRight: '0.32em' }}></span>
                  <span style={{ fontSize: 'clamp(9px, 0.83vw, 16px)', fontWeight: '400' }}>
                   {!isPriceLoading && data?.linear_feet && data?.price_per_lf
                  ? `${Math.round(Number(data.linear_feet))} LF avg $${Math.round(Number(data.price_per_lf)).toLocaleString()}/LF`
                  : ''}
                  </span>
                  <span style={{ fontSize: 'clamp(14px, 1.1vw, 18px)', position: 'relative', top: '-0.60em', lineHeight: 1 }}></span>
                </div>
              
            </div>
            )}

            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(255,255,255,0.25) transparent',
                // PSD puts the selections, the buttons and the footer 62pt in
                // from the panel edge while the gold block starts at 47pt.
                ...(isDistributor
                  ? { marginLeft: psd(15), marginRight: psd(15) }
                  : null),
              }}
            >
              <p
                style={{
                  fontFamily: "'Calibri','Lato',sans-serif",
                  // PSD 36pt. The cost snapshot keeps its own scale.
                  fontSize: isDistributor ? psd(36) : 'clamp(12px, 1.1vw, 18px)',
                  color: 'rgb(180 179 176)',
                  margin: 0,
                   marginLeft: '0.6rem',
                   marginBottom: '1.5%'
                }}
              >
                {isDistributor ? 'Your current selections -' : 'Your selections of'}
              </p>

              {listedSelections.map((sel, i) => (
                <div key={i} style={{ marginBottom: '1.5%', marginLeft: '1rem' }}>
                  <div
                    style={{
                      fontFamily: "'Calibri','Lato',sans-serif",
                      // PSD 24pt for the category, 36pt for its value.
                      fontSize: isDistributor ? psd(24) : 'clamp(9px, 0.7vw, 12px)',
                      fontWeight: '300',
                      color: '#d5d5d5',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {sel.category === 'Appliances' ? 'Appliance' : sel.category}:
                  </div>
                  <div
                    style={{
                      fontFamily: "'Calibri','Lato',sans-serif",
                      fontSize: isDistributor ? psd(36) : 'clamp(12px, 2.0vw, 16px)',
                      fontWeight: '300',
                      color: '#efefef',
                      lineHeight: 1.3,
                      paddingLeft: '4.2%',
                    }}
                  >
                    {sel.category === 'Appliances'
                      ? sel.value
                      : `${collectionLabel(sel.collection)} ${sel.value}`.trim()}
                  </div>
                </div>
              ))}
            </div>

            {/* ── DISCLAIMER — fixed at bottom, NEVER shrinks or moves ── */}
            {/* It explains the asterisk on the price, so it goes where the
                price goes. */}
            {!isDistributor && (
            <div
              style={{
                flexShrink: 0,
                display: 'flex',
                gap: '3%',
                alignItems: 'flex-start',
              }}
            >
              <span
                style={{
                  color: '#afafaf',
                  fontSize: 'clamp(9px, 0.9vw, 13px)',
                  lineHeight: 1.5,
                  flexShrink: 0,
                }}
              >
                *
              </span>
              <span
                style={{
                  fontFamily: "'Calibri','Lato',sans-serif",
                  fontSize: 'clamp(8px, 0.7vw, 14px)',
                  color: '#afafaf',
                  lineHeight: 1.5,
                  letterSpacing: '0.01em',
                  textAlign: 'left',
                  display: 'block',
                  flexShrink: 0,
                  width: 'calc(100% - 1.2em)',
                }}
              >
                Price reflects fixed interior options of UV Maple
                Plywood as{' '}
                <a
                  href="https://www.domaincabinets.com/Custom-Cabinet-Box-s/748.htm"
                  style={{ color: '#afafaf', textDecoration: 'underline' }}
                >
                  cabinet box material, 
                </a>
                {' '}Maple Standard Grade{' '}
                <a
                  href="https://www.domaincabinets.com/Custom-Drawer-Box-s/738.htm"
                  style={{ color: '#afafaf', textDecoration: 'underline' }}
                >
                  drawer box
                </a>
                , and{' '}
                Blum® BluMotion standard hardware.
              </span>
            </div>
            )}

            {/* ── BUTTONS — fixed at bottom, never shrinks ── */}
            <div
              style={{
                flexShrink: 0,
                display: 'flex',
                gap: '2%',
                marginTop: 'clamp(14px, 1.7vw, 21px)',
                ...(isDistributor
                  ? { marginLeft: psd(15), marginRight: psd(15) }
                  : null),
                
                
              }}
            >
              {['DESIGN', 'ITEM LIST'].map((label) => (
                <button
                  key={label}
                  onClick={() => handleButtonClick(label)}
                  disabled={isGeneratingPDF && label === 'ITEM LIST'}
                  style={{
                    flex: 1,
                    background: '#fff',
                    color: '#111',
                    // PSD "Small Button Left/Right": 202×78pt.
                    height: isDistributor ? psd(78) : '36px',
                    border: '1px solid #333',
                    paddingTop: '2.5px',
                    fontFamily: "'Bebas Neue',sans-serif",
                    fontSize: isDistributor ? psd(42) : 'clamp(12px, 1.5vw, 18px)',
                    letterSpacing: '0.1em',
                    transform: 'translateY(-1px)',
                    cursor: isGeneratingPDF && label === 'ITEM LIST' ? 'wait' : 'pointer',
                    lineHeight: 1,
                    // 3pt corners on the cost snapshot, 37.5pt on the
                    // distributor one — half its height, i.e. a full pill.
                    borderRadius: isDistributor ? '999px' : '3px',
                    opacity: isGeneratingPDF && label === 'ITEM LIST' ? 0.6 : 1,
                  }}
                >
                  {isGeneratingPDF && label === 'ITEM LIST' ? 'BUILDING...' : label}
                </button>
              ))}
            </div>

            {/* ── ESTIMATOR CALL-TO-ACTION — distributor only ── */}
            {isDistributor && (
              <div
                style={{
                  flexShrink: 0,
                  textAlign: 'center',
                  marginLeft: psd(15),
                  marginRight: psd(15),
                }}
              >
                {/* 32pt Calibri Light, white. */}
                <p
                  style={{
                    fontFamily: "'Calibri','Lato',sans-serif",
                    fontSize: psd(32),
                    color: '#fff',
                    lineHeight: 1,
                    margin: 0,
                    marginTop: psd(120),
                  }}
                >
                  Want to budget YOUR project?
                </p>

                {/* "Rectangle - Cost Estimator": 422×98pt, 48pt corners — again
                    half the height, so a pill. */}
                <button
                  type="button"
                  onClick={() => handleButtonClick('COST ESTIMATOR')}
                  style={{
                    width: '100%',
                    background: GOLD,
                    color: '#fff',
                    height: psd(98),
                    border: 'none',
                    marginTop: psd(14),
                    fontFamily: "'Bebas Neue',sans-serif",
                    fontSize: psd(42),
                    letterSpacing: '0.1em',
                    textIndent: '0.1em',
                    cursor: 'pointer',
                    lineHeight: 1,
                    borderRadius: '999px',
                  }}
                >
                  <span style={{ display: 'inline-block', transform: 'translateY(2px)' }}>
                    COST ESTIMATOR
                  </span>
                </button>

                {/* Two fonts in one line, as the PSD has it: Calibri Light for
                    "Visualizer by", Segoe Script for the name, and a separate
                    rule under the name alone — not under the whole line. The
                    PSD names that rule "Underline Link Domain Cabinets", so the
                    name is the link; it points at the main site, the same host
                    the disclaimer links already use.
                    Colour is inherited so the link keeps the PSD's white rather
                    than the browser's default blue. */}
                <p
                  style={{
                    fontFamily: "'Calibri','Lato',sans-serif",
                    fontSize: psd(24),
                    color: '#fff',
                    lineHeight: 1,
                    margin: 0,
                    marginTop: psd(46),
                  }}
                >
                  Visualizer by{' '}
                  <a
                    href="https://www.domaincabinets.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontFamily: "'Segoe Script','Bradley Hand',cursive",
                      color: 'inherit',
                      textDecoration: 'underline',
                      textDecorationThickness: '1px',
                      textUnderlineOffset: '2px',
                    }}
                  >
                    Domain Cabinets
                  </a>
                </p>
              </div>
            )}
          </div>
        </div>

       
          {/* Drawn rather than an <img>: the old PNG had all four corners
              rounded inside the image, so CSS could not square off the side
              that meets the border. Only the outer (right) corners are round. */}
          <button
            type="button"
            aria-label="close"
            onClick={() => setIsPanelOpen(false)}
            style={{
              position: 'absolute',
              // Left edge sits on the border line: the box's right edge is
              // INSET_RIGHT from the panel edge, so an arrow of ARROW_W placed at
              // (INSET_RIGHT - ARROW_W) meets it; ARROW_OVERLAP tucks it under.
              right: `${INSET_RIGHT - ARROW_W + ARROW_OVERLAP}px`,
              bottom: '4.4%',
              width: `${ARROW_W}px`,
              height: `${ARROW_H}px`,
              background: '#fff',
              border: 'none',
              borderRadius: '0 1px 1px 0',
              cursor: 'pointer',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="8" height="14" viewBox="0 0 8 14" aria-hidden="true">
              <path d="M1 1l6 6-6 6" fill="none" stroke="#111" strokeWidth="1.3" />
            </svg>
          </button>
       
      </div>
    </>
  );
}
